import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getDependencyRecords } from '../utils/dependencyUtils';
import { fetchMilestoneMap, fetchObjectives } from '../../store/map3DataSlice';
import type { RootState, AppDispatch } from '../../store/store';
import type { MilestoneMap1, Objective1 } from '@mm-shared/types';
import type { UserProfile1 } from '@mm-shared/types/UserProfile1';

/**
 * Helper: Check if user has access to a map
 *
 * TODO: Either verify Firestore rules more rigorously handle this, or use a different approach long term (e.g. move to Cloud Function, etc.)
 */
function userHasAccess(mapUuid: string, userProfile: UserProfile1): boolean {
  return (
    !!userProfile?.myMilestoneMaps?.some((m) => m.uuid === mapUuid) ||
    !!userProfile?.sharedMilestoneMaps?.some((m) => m.uuid === mapUuid)
  );
}

/**
 * Traverses all maps/objectives/results starting from the current map, following dependencies,
 * and ensures all referenced maps/objectives are loaded. Logs missing access. Returns all referenced uuids.
 *
 * @internal exported for testing only
 *
 * RC-1 FIX: Uses getState() to read fresh Redux state after each async dispatch, rather than
 * relying on the stale closure values of allMaps/allObjectives. This is the same pattern that
 * Redux thunks use for reading state mid-operation.
 *
 * RC-2 FIX: Accepts traversalVersion/versionRef to detect stale traversals. After each async
 * operation, checks whether a newer traversal has been started (version mismatch) and bails out
 * early to avoid producing stale results. (INV-26)
 */
export async function traverseAndLoadAllRelatives({
  firstMapUuid,
  getState,
  traversalVersion,
  versionRef,
  userProfile,
  dispatch,
}: {
  firstMapUuid: string;
  getState: () => RootState;
  traversalVersion: number;
  versionRef: React.MutableRefObject<number>;
  userProfile: UserProfile1;
  dispatch: AppDispatch;
}) {
  const targetMapUuids = new Set([firstMapUuid]);
  const checkedMapUuids = new Set<string>();
  const targetObjectiveUuids = new Set<string>();
  const targetResultUuids = new Set<string>();
  while (true) {
    const nextMapUuid = Array.from(targetMapUuids).find((uuid) => !checkedMapUuids.has(uuid));
    if (!nextMapUuid) break;
    checkedMapUuids.add(nextMapUuid);
    if (!userHasAccess(nextMapUuid, userProfile)) {
      // console.log('No access to map', nextMapUuid);
      continue;
    }
    // Read fresh state (INV-25: reads fresh state after each async dispatch)
    let allMaps = getState().map3Data.milestoneMaps;
    // Fetch map if not loaded
    let milestoneMap: MilestoneMap1 | undefined = allMaps.find((m) => m.uuid === nextMapUuid);
    if (!milestoneMap) {
      try {
        await dispatch(fetchMilestoneMap(nextMapUuid));
        // Note: without .unwrap(), rejected thunks do not throw. This try/catch
        // is preserved from existing code for safety but does not currently catch rejections.
      } catch (e) {}
      // INV-26: Bail out if a newer traversal has started since we awaited.
      if (versionRef.current !== traversalVersion) return null;
      // Read fresh state after the async dispatch (INV-25)
      allMaps = getState().map3Data.milestoneMaps;
      milestoneMap = allMaps.find((m) => m.uuid === nextMapUuid);
    }
    if (!milestoneMap) continue;
    // Load ALL objectives for complete data (needed for tree view hierarchy)
    // Previously filtered out parentObjectiveUuids, but tree view needs major objectives
    const objectiveUuids = milestoneMap!.objectiveUuids;
    // Read fresh objectives state
    let allObjectives = getState().map3Data.objectives;
    // Fetch objectives if not loaded
    const missingObjectiveUuids = objectiveUuids.filter(
      (uuid) => !allObjectives.find((obj) => obj.uuid === uuid),
    );
    if (missingObjectiveUuids.length > 0) {
      try {
        await dispatch(fetchObjectives(missingObjectiveUuids));
        // Note: without .unwrap(), rejected thunks do not throw. This try/catch
        // is preserved from existing code for safety but does not currently catch rejections.
      } catch (e) {}
      // INV-26: Bail out if a newer traversal has started since we awaited.
      if (versionRef.current !== traversalVersion) return null;
      // Read fresh objectives state after the async dispatch (INV-25)
      allObjectives = getState().map3Data.objectives;
    }
    const objectives = objectiveUuids
      .map((uuid) => allObjectives.find((obj) => obj.uuid === uuid))
      .filter((obj): obj is Objective1 => Boolean(obj));
    for (const obj of objectives) {
      for (const result of obj.results) {
        for (const dep of result.dependencies || []) {
          if (dep.targetMapUuid) targetMapUuids.add(dep.targetMapUuid);
          if (dep.targetObjectiveUuid) targetObjectiveUuids.add(dep.targetObjectiveUuid);
          if (dep.targetResultUuid) targetResultUuids.add(dep.targetResultUuid);
        }
      }
    }
  }
  return { targetMapUuids, targetObjectiveUuids, targetResultUuids };
}

/**
 * Hook to ensure all maps/objectives referenced by cross-map dependencies are loaded.
 * Returns { loading, allDependencyRecords } for the current map and all related maps.
 *
 * First-render flow: if currentMap is undefined (map not yet loaded), the effect runs once
 * and returns early. When the map subsequently loads into Redux, currentMap transitions
 * from undefined to defined, which re-triggers the effect and begins the traversal.
 */
export function useAllRelativesData(mapUuid: string) {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore();
  // getState is referentially stable across renders — it is a method on the store singleton.
  // This makes it safe to include in the useEffect dependency array.
  const getState = store.getState as () => RootState;
  const allMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const currentMap = allMaps.find((m) => m.uuid === mapUuid || m.shortId === mapUuid);
  const [loading, setLoading] = useState(false);
  const [relatedMapUuids, setRelatedMapUuids] = useState<string[]>([]);

  // RC-2 FIX: Version counter for stale traversal detection (INV-26).
  // Incrementing this ref at the start of each effect run marks any in-flight traversal as stale.
  const traversalVersionRef = useRef(0);

  useEffect(() => {
    // RC-2 FIX: Increment version to invalidate any prior in-flight traversal.
    const currentVersion = ++traversalVersionRef.current;

    async function fetchAllRelatives() {
      if (!currentMap) return;
      setLoading(true);
      const result = await traverseAndLoadAllRelatives({
        firstMapUuid: currentMap.uuid,
        getState,
        traversalVersion: currentVersion,
        versionRef: traversalVersionRef,
        userProfile,
        dispatch,
      });
      // Only apply results if this traversal is still current (not superseded).
      if (result !== null && traversalVersionRef.current === currentVersion) {
        setRelatedMapUuids(Array.from(result.targetMapUuids));
        setLoading(false);
      }
    }
    fetchAllRelatives();
    // RC-2 FIX: Increment version on cleanup so any in-flight traversal bails out via INV-26.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: we want the live ref value at cleanup time, not a snapshot
    return () => { traversalVersionRef.current++; };
    // RC-2 FIX: allMaps and allObjectives intentionally omitted from deps.
    // The traversal reads fresh state via getState() after each async dispatch (INV-25),
    // so it does not need these as effect triggers. Including them would cause re-execution
    // on every dispatch, creating a cascade of concurrent traversals (the original RC-2 bug).
    // The effect re-runs when currentMap changes (navigation to a new map).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMap, userProfile, dispatch, getState]);

  // TODO:
  //   1. Determine if this should be replaced with Redux state.
  //   2. Determin if Data3 needs a way to refresh when other data gets updated
  // Get all dependency records for current map and all related maps
  const allDependencyRecords = useMemo(() => {
    if (!currentMap) return [];
    // Always include current map
    // (If you want to include all relatives, you could expand this to all loaded maps in targetMapUuids)
    return getDependencyRecords(currentMap, allObjectives, allMaps);
  }, [currentMap, allMaps, allObjectives]);

  const allRelatedMilestoneMaps = useMemo(() => {
    // RC-4 FIX: Only match on full UUID. The shortId check was removed because relatedMapUuids
    // contains full UUIDs from the traversal, and a shortId coincidentally matching another
    // map's full UUID would incorrectly include unrelated maps.
    const uuids = new Set([...relatedMapUuids, mapUuid]);
    return allMaps.filter((m) => uuids.has(m.uuid));
  }, [allMaps, relatedMapUuids, mapUuid]);

  return { loading, allDependencyRecords, allRelatedMilestoneMaps };
}
